import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Award, ChevronDown, ChevronUp, Search, Sliders, UserPlus, Users, X } from "react-feather";
import { errorToast, successToast } from "../../shared/Toast";
import { C } from "../reporting/reportingStyles";
import { assignMembership, bulkAssignMemberships } from "../../utils/membershipApi";
import { getUserListing } from "../../utils/apiCalls";
import { formatDiscount } from "./membershipConstants";
import {
    Cols, Field, Section,
    hintStyle, warnStyle, inputStyle, primaryBtnStyle, secondaryBtnStyle, withDisabled,
} from "../common/FormKit";

/**
 * Assign a membership to one customer, or to a batch of them.
 *
 * Three things about this form are worth knowing before changing it.
 *
 * Customers are chosen by SEARCHING the existing customer base, never typed in. A
 * membership hangs off a real `User` row — the same row the pickup app logs into and the
 * POS resolves by phone — so inventing one here would create a card nobody can use.
 *
 * ONE customer goes to POST /api/memberships, which upserts: a customer who already holds a
 * card is re-issued onto the existing row, keeping the card number and QR, with the change
 * recorded in their history. That is what makes "revoke, then re-issue later" work without
 * handing out a new number.
 *
 * MANY customers go to POST /api/memberships/bulk, which deliberately does NOT do that. It
 * skips anyone who already holds a card and reports them. An admin ticking everyone a search
 * turned up would otherwise move existing members onto whatever tier was picked for the
 * newcomers, and nothing on screen would say so.
 *
 * Layout follows the Digital Signage "Configure screen" look (common/FormKit): three titled
 * sections — who, which tier, and the optional extras folded away until asked for.
 */

const nameOf = (user) => [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "—";

const initialsOf = (user) => {
    const parts = [user?.firstName, user?.lastName].filter(Boolean);
    if (!parts.length) return "?";
    return parts.map((p) => String(p).trim().charAt(0).toUpperCase()).join("").slice(0, 2);
};

const Avatar = ({ user, selected }) => (
    <span
        aria-hidden
        style={{
            width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 700,
            background: selected ? C.coral : C.borderLight,
            color: selected ? C.white : C.textMed,
        }}
    >
        {initialsOf(user)}
    </span>
);

/** "max SAR 500, 3/day, 20/month", or "no limits". */
const tierLimitsText = (type) => [
    type?.maxOrderValue != null && `max SAR ${type.maxOrderValue}`,
    type?.maxDailyOrders != null && `${type.maxDailyOrders}/day`,
    type?.maxMonthlyOrders != null && `${type.maxMonthlyOrders}/month`,
].filter(Boolean).join(", ") || "no limits";

const toNullableNumber = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

const AssignMembershipModal = ({ merchantId, types, onClose, onSaved, onBulkDone }) => {
    const [search, setSearch] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    // Keyed by id and holding the whole user, so a chosen customer survives the next search
    // replacing `results` — otherwise ticking someone, searching again, and submitting would
    // quietly assign a different set than the one on screen.
    const [selectedUsers, setSelectedUsers] = useState(() => new Map());
    const [isSaving, setIsSaving] = useState(false);
    const [report, setReport] = useState(null);
    const [showMore, setShowMore] = useState(false);

    const activeTypes = useMemo(() => (types || []).filter((t) => t.isActive), [types]);

    const [form, setForm] = useState({
        membershipTypeId: "",
        status: "active",
        expiryDate: "",
        employeeId: "",
        maxOrderValue: "",
        maxDailyOrders: "",
        maxMonthlyOrders: "",
        reason: "",
        internalNote: "",
    });

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    // Default to the first active tier so the common case is one click.
    useEffect(() => {
        if (!form.membershipTypeId && activeTypes.length) {
            set({ membershipTypeId: String(activeTypes[0].id) });
        }
    }, [activeTypes, form.membershipTypeId]);

    const runSearch = useCallback(async (term) => {
        if (!term || term.trim().length < 2) { setResults([]); return; }
        setSearching(true);
        // userType is "Customer" — the value the column actually holds. It said "user" for a
        // long time, which is not one of the allowed values, so the server matched nothing
        // and every search came back empty.
        //
        // merchantId scopes the picker: the server reads it as "belongs to this merchant OR
        // has a MerchantCustomers row for it", so a card cannot be handed to somebody who has
        // never been here. A merchant-scoped admin has this forced from their token anyway;
        // it only changes what a platform admin sees.
        const data = await getUserListing({
            search: term.trim(),
            limit: 10,
            userType: "Customer",
            // Number, like the submit payload below: the prop arrives as a string from
            // localStorage, and the two should not disagree about the same merchant.
            merchantId: Number(merchantId),
        });
        setSearching(false);
        setResults(data?.users || []);
    }, [merchantId]);

    useEffect(() => {
        const timer = setTimeout(() => runSearch(search), 350);
        return () => clearTimeout(timer);
    }, [search, runSearch]);

    const chosenType = activeTypes.find((t) => String(t.id) === String(form.membershipTypeId));

    // Preview the expiry the API will fill in, so an admin is never surprised by a card
    // that expires in three months because the tier said so.
    const impliedExpiry = useMemo(() => {
        if (form.expiryDate) return null;
        const months = Number(chosenType?.defaultValidityMonths);
        if (!Number.isFinite(months) || months <= 0) return "never expires";
        const d = new Date();
        d.setMonth(d.getMonth() + months);
        return `${d.toLocaleDateString()} (${months} months, from the tier)`;
    }, [form.expiryDate, chosenType]);

    const chosen = useMemo(() => [...selectedUsers.values()], [selectedUsers]);
    const isBulk = chosen.length > 1;

    // How many of the optional fields carry a value, so the folded section says so.
    const extrasSet = ["maxOrderValue", "maxDailyOrders", "maxMonthlyOrders", "reason", "internalNote"]
        .filter((k) => String(form[k] ?? "").trim() !== "").length;

    const toggleUser = (user) => setSelectedUsers((prev) => {
        const next = new Map(prev);
        if (next.has(user.id)) next.delete(user.id); else next.set(user.id, user);
        return next;
    });

    const selectAllResults = () => setSelectedUsers((prev) => {
        const next = new Map(prev);
        results.forEach((u) => next.set(u.id, u));
        return next;
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!merchantId) { errorToast("Select a merchant first."); return; }
        if (!chosen.length) { errorToast("Search for and select at least one customer."); return; }
        if (!form.membershipTypeId) { errorToast("Choose a tier."); return; }

        // Everything both endpoints share. Omitted vs explicit null are different
        // instructions to the API: omitted means "use the tier's default validity", null
        // means "never expires". Sending "" for an untouched field would be the wrong one
        // of the two, so leave the key out.
        const shared = {
            merchantId: Number(merchantId),
            membershipTypeId: Number(form.membershipTypeId),
            status: form.status,
            ...(form.expiryDate ? { expiryDate: form.expiryDate } : {}),
            maxOrderValue: toNullableNumber(form.maxOrderValue),
            maxDailyOrders: toNullableNumber(form.maxDailyOrders),
            maxMonthlyOrders: toNullableNumber(form.maxMonthlyOrders),
            reason: form.reason || null,
            internalNote: form.internalNote || null,
        };

        setIsSaving(true);

        if (isBulk) {
            // employeeId is deliberately not sent: it identifies one person, so a single
            // value across a batch is wrong for everyone but at most one of them. The
            // field is hidden for the same reason.
            const result = await bulkAssignMemberships({
                ...shared,
                userIds: chosen.map((u) => u.id),
            });
            setIsSaving(false);
            // The report replaces the form rather than closing over it — a toast saying
            // "12 assigned" would hide which four were skipped and why.
            if (result) setReport(result);
            return;
        }

        const saved = await assignMembership({
            ...shared,
            userId: chosen[0].id,
            employeeId: form.employeeId || null,
        });
        setIsSaving(false);

        if (saved) {
            successToast(`Membership ${saved.membershipRef} assigned`);
            onSaved?.(saved);
        }
    };

    if (report) {
        // The list needs refreshing, but there is no single "new member" to open — so this
        // hands the report back rather than navigating anywhere.
        return <BulkAssignReport report={report} onDone={() => onBulkDone?.(report)} />;
    }

    const submitDisabled = isSaving || !activeTypes.length;
    const searchTooShort = search.trim().length < 2;

    return (
        <Modal isOpen toggle={onClose} centered size="lg">
            <ModalHeader toggle={onClose} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 10, fontWeight: 700, color: C.text }}>
                    <span
                        aria-hidden
                        style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                        }}
                    >
                        <UserPlus size={16} color={C.white} />
                    </span>
                    Assign Membership
                </span>
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody style={{ background: C.bg, display: "flex", flexDirection: "column", gap: 14 }}>
                    {!activeTypes.length && (
                        <div style={{ padding: 12, borderRadius: 10, background: C.orangeLight, fontSize: 13, color: C.text }}>
                            This merchant has no active tiers. Create and activate one first.
                        </div>
                    )}

                    {/* ── 1. Customers ─────────────────────────────────────────── */}
                    <Section
                        icon={Users}
                        title="Customers"
                        subtitle="Only this merchant's existing customers can be given a card — the membership hangs off the account they log in with."
                        aside={chosen.length > 0 && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: C.coral, whiteSpace: "nowrap" }}>
                                {chosen.length} selected
                            </span>
                        )}
                    >
                        <div style={{ position: "relative" }}>
                            <Search
                                size={15}
                                color={C.textLight}
                                aria-hidden
                                style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
                            />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name, phone or email (at least 2 characters)"
                                style={{ ...inputStyle, paddingLeft: 34 }}
                            />
                        </div>

                        {searching && <div style={{ fontSize: 12, color: C.textMed }}>Searching…</div>}

                        {!searching && results.length > 0 && (
                            <div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                                    <span style={{ fontSize: 12, color: C.textLight }}>
                                        {results.length} result{results.length === 1 ? "" : "s"} · tick as many as you like; ticks are kept as you search again
                                    </span>
                                    {results.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={selectAllResults}
                                            style={{ ...secondaryBtnStyle, padding: "4px 10px", fontSize: 12 }}
                                        >
                                            Select all {results.length}
                                        </button>
                                    )}
                                </div>
                                <div style={{ maxHeight: 220, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 10, background: C.white }}>
                                    {results.map((u) => {
                                        const isSelected = selectedUsers.has(u.id);
                                        return (
                                            <label
                                                key={u.id}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 10, margin: 0,
                                                    padding: "8px 12px", cursor: "pointer",
                                                    borderBottom: `1px solid ${C.borderLight}`,
                                                    background: isSelected ? C.coralLight : "transparent",
                                                    transition: "background 0.15s",
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleUser(u)}
                                                    style={{ width: 16, height: 16, accentColor: C.coral, flexShrink: 0 }}
                                                />
                                                <Avatar user={u} selected={isSelected} />
                                                <span style={{ minWidth: 0 }}>
                                                    <span style={{ display: "block", fontWeight: 600, fontSize: 13, color: C.text }}>{nameOf(u)}</span>
                                                    <span style={{ display: "block", fontSize: 11, color: C.textLight }}>
                                                        {u.phoneNo || "—"}{u.email ? ` · ${u.email}` : ""}
                                                    </span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {!searching && !searchTooShort && results.length === 0 && (
                            <div style={{ fontSize: 12, color: C.textMed }}>No customers found.</div>
                        )}

                        {chosen.length > 0 && (
                            <div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                        Getting a card
                                    </span>
                                    {chosen.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => setSelectedUsers(new Map())}
                                            style={{ ...secondaryBtnStyle, padding: "4px 10px", fontSize: 12 }}
                                        >
                                            Clear all
                                        </button>
                                    )}
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {chosen.map((u) => (
                                        <span
                                            key={u.id}
                                            title={u.phoneNo || undefined}
                                            style={{
                                                display: "inline-flex", alignItems: "center", gap: 6,
                                                padding: "4px 6px 4px 10px", borderRadius: 999,
                                                background: C.coralLight, border: `1px solid ${C.coral}`,
                                                fontSize: 12, fontWeight: 600, color: C.coral,
                                            }}
                                        >
                                            {nameOf(u)}
                                            <button
                                                type="button"
                                                onClick={() => toggleUser(u)}
                                                aria-label={`Remove ${nameOf(u)}`}
                                                style={{
                                                    border: "none", background: "transparent", padding: 2, cursor: "pointer",
                                                    display: "inline-flex", color: C.coral,
                                                }}
                                            >
                                                <X size={13} aria-hidden />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {isBulk && (
                            <div style={{ ...warnStyle, marginTop: 0, padding: "8px 12px", borderRadius: 8, background: C.orangeLight }}>
                                Anyone here who already holds a card at this merchant will be left as they
                                are — their tier will not change. You will get a list afterwards.
                            </div>
                        )}
                    </Section>

                    {/* ── 2. Membership ────────────────────────────────────────── */}
                    <Section icon={Award} title="Membership" subtitle="Which tier, and how the card starts.">
                        <div role="radiogroup" aria-label="Tier" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                            {activeTypes.map((t) => {
                                const selected = String(t.id) === String(form.membershipTypeId);
                                const rateCount = (t.discountRules || []).length;
                                return (
                                    <label
                                        key={t.id}
                                        style={{
                                            display: "flex", gap: 10, alignItems: "flex-start", margin: 0,
                                            padding: "12px 14px", borderRadius: 10, cursor: "pointer",
                                            border: `1.5px solid ${selected ? C.coral : C.border}`,
                                            background: selected ? C.coralLight : C.white,
                                            transition: "all 0.15s",
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            name="membershipTypeId"
                                            value={t.id}
                                            checked={selected}
                                            onChange={() => set({ membershipTypeId: String(t.id) })}
                                            style={{ marginTop: 3, accentColor: C.coral }}
                                        />
                                        <span style={{ minWidth: 0 }}>
                                            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, background: t.cardColour || C.coral, flexShrink: 0 }} />
                                                <span style={{ fontSize: 13, fontWeight: 700, color: selected ? C.coral : C.text }}>{t.name}</span>
                                            </span>
                                            <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.text, marginTop: 3 }}>
                                                {formatDiscount(t.discountType, t.discountValue)} off
                                                {rateCount > 0 && <span style={{ fontWeight: 500, color: C.textMed }}> · {rateCount} custom rate{rateCount === 1 ? "" : "s"}</span>}
                                            </span>
                                            <span style={{ display: "block", fontSize: 11, color: C.textLight, marginTop: 2 }}>
                                                {Number(t.defaultValidityMonths) > 0 ? `${t.defaultValidityMonths}-month cards` : "Cards do not expire"}
                                                {" · "}{tierLimitsText(t)}
                                            </span>
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                        {chosenType?.requiresPhoto && (
                            <span style={{ ...warnStyle, marginTop: -4 }}>
                                This tier requires a verified photo. Until one is approved on the member's
                                profile, the card gives no discount.
                            </span>
                        )}

                        <div role="radiogroup" aria-label="Starting status" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            {[
                                { value: "active", label: "Active", hint: "Discount works immediately." },
                                { value: "pending", label: "Pending", hint: "No discount until activated." },
                            ].map((o) => {
                                const selected = form.status === o.value;
                                return (
                                    <label
                                        key={o.value}
                                        style={{
                                            display: "flex", gap: 10, alignItems: "flex-start", margin: 0,
                                            padding: "10px 12px", borderRadius: 10, cursor: "pointer",
                                            border: `1.5px solid ${selected ? C.coral : C.border}`,
                                            background: selected ? C.coralLight : C.white,
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            name="status"
                                            value={o.value}
                                            checked={selected}
                                            onChange={() => set({ status: o.value })}
                                            style={{ marginTop: 3, accentColor: C.coral }}
                                        />
                                        <span>
                                            <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: selected ? C.coral : C.text }}>{o.label}</span>
                                            <span style={{ display: "block", fontSize: 12, color: C.textLight }}>{o.hint}</span>
                                        </span>
                                    </label>
                                );
                            })}
                        </div>

                        <Cols>
                            <Field
                                label="Expiry date"
                                hint={impliedExpiry ? `Leave blank and this card ${impliedExpiry}.` : null}
                            >
                                <input
                                    type="date"
                                    value={form.expiryDate}
                                    onChange={(e) => set({ expiryDate: e.target.value })}
                                    style={inputStyle}
                                />
                            </Field>
                            {/* An employee ID identifies one person, so it has no meaning across a
                                batch — the bulk endpoint refuses it too. */}
                            {!isBulk && (
                                <Field label="Employee ID">
                                    <input
                                        type="text"
                                        value={form.employeeId}
                                        onChange={(e) => set({ employeeId: e.target.value })}
                                        placeholder="Optional — for staff tiers"
                                        style={inputStyle}
                                    />
                                </Field>
                            )}
                        </Cols>
                    </Section>

                    {/* ── 3. More options ──────────────────────────────────────── */}
                    <Section
                        icon={Sliders}
                        title="More options"
                        subtitle={
                            extrasSet > 0
                                ? `${extrasSet} set — limits just for ${isBulk ? "these members" : "this member"}, reason and note.`
                                : `Limits just for ${isBulk ? "these members" : "this member"}, a reason and an internal note. All optional.`
                        }
                        aside={(
                            <button
                                type="button"
                                onClick={() => setShowMore((v) => !v)}
                                aria-expanded={showMore}
                                style={{ ...secondaryBtnStyle, padding: "5px 12px", fontSize: 12 }}
                            >
                                {showMore ? <>Hide <ChevronUp size={13} aria-hidden /></> : <>Show <ChevronDown size={13} aria-hidden /></>}
                            </button>
                        )}
                    >
                        {showMore && (
                            <>
                                <span style={{ ...hintStyle, marginTop: -6 }}>
                                    Leave blank to use the tier's limits
                                    {chosenType && <>{" — currently "}{tierLimitsText(chosenType)}</>}
                                    . Any value entered here is recorded in the member's history, with what it replaced.
                                </span>
                                <Cols min={150}>
                                    <Field label="Max order value (SAR)">
                                        <input
                                            type="number" step="0.01"
                                            value={form.maxOrderValue}
                                            onChange={(e) => set({ maxOrderValue: e.target.value })}
                                            placeholder="Tier's limit"
                                            style={inputStyle}
                                        />
                                    </Field>
                                    <Field label="Orders per day">
                                        <input
                                            type="number"
                                            value={form.maxDailyOrders}
                                            onChange={(e) => set({ maxDailyOrders: e.target.value })}
                                            placeholder="Tier's limit"
                                            style={inputStyle}
                                        />
                                    </Field>
                                    <Field label="Orders per month">
                                        <input
                                            type="number"
                                            value={form.maxMonthlyOrders}
                                            onChange={(e) => set({ maxMonthlyOrders: e.target.value })}
                                            placeholder="Tier's limit"
                                            style={inputStyle}
                                        />
                                    </Field>
                                </Cols>
                                <Cols>
                                    <Field label="Reason" hint="Recorded in the member's history. Admin eyes only.">
                                        <input
                                            type="text"
                                            value={form.reason}
                                            onChange={(e) => set({ reason: e.target.value })}
                                            placeholder="Why this membership was granted"
                                            style={inputStyle}
                                        />
                                    </Field>
                                    <Field label="Internal note" hint="Never shown to the customer.">
                                        <textarea
                                            rows={2}
                                            value={form.internalNote}
                                            onChange={(e) => set({ internalNote: e.target.value })}
                                            style={{ ...inputStyle, resize: "vertical" }}
                                        />
                                    </Field>
                                </Cols>
                            </>
                        )}
                    </Section>
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.borderLight}`, justifyContent: "space-between" }}>
                    <span style={{ fontSize: 12, color: C.textMed, minWidth: 0 }}>
                        {chosenType && chosen.length > 0
                            ? `${chosenType.name} · ${formatDiscount(chosenType.discountType, chosenType.discountValue)} off → ${chosen.length} customer${chosen.length === 1 ? "" : "s"}`
                            : "Pick at least one customer and a tier."}
                    </span>
                    <span style={{ display: "inline-flex", gap: 8 }}>
                        <button type="button" onClick={onClose} style={secondaryBtnStyle}>Cancel</button>
                        <button type="submit" disabled={submitDisabled} style={withDisabled(primaryBtnStyle, submitDisabled)}>
                            {isSaving
                                ? "Assigning…"
                                : isBulk ? `Assign ${chosen.length} memberships` : "Assign membership"}
                        </button>
                    </span>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

const REPORT_LABELS = {
    assigned: "Given a card",
    already_member: "Already had a card",
    not_found: "Customer not found",
    failed: "Could not be assigned",
};

/**
 * What actually happened, per customer.
 *
 * Shown instead of a toast because a bulk assign almost never does exactly what was asked:
 * the interesting part is who was skipped and why, and a count alone hides it.
 */
const BulkAssignReport = ({ report, onDone }) => {
    const { assignedCount = 0, totalCount = 0, results = [] } = report || {};
    const notAssigned = results.filter((r) => r.status !== "assigned");

    const countOf = (status) => results.filter((r) => r.status === status).length;
    // Mutually exclusive and summing to totalCount, so the three numbers can be read
    // together. An "already had one" customer is not also counted under a general
    // "not assigned" — that made one person look like two.
    const tiles = [
        { label: "Given a card", value: assignedCount, bg: C.greenLight, fg: C.green },
        { label: "Already had one", value: countOf("already_member"), bg: C.orangeLight, fg: C.orange },
        {
            label: "Not found or failed",
            value: totalCount - assignedCount - countOf("already_member"),
            bg: C.bg,
            fg: C.textMed,
        },
    ];

    return (
        <Modal isOpen toggle={onDone} centered size="lg">
            <ModalHeader toggle={onDone} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
                <span style={{ fontWeight: 700, color: C.text }}>Bulk assign — results</span>
            </ModalHeader>
            <ModalBody>
                <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                    {tiles.map((t) => (
                        <div key={t.label} style={{ flex: 1, padding: 12, borderRadius: 10, background: t.bg }}>
                            <div style={{ fontSize: 22, fontWeight: 700, color: t.fg }}>{t.value}</div>
                            <div style={{ fontSize: 12, color: C.textMed }}>{t.label}</div>
                        </div>
                    ))}
                </div>

                {notAssigned.length === 0 ? (
                    <div style={{ fontSize: 13, color: C.textMed }}>
                        Every customer selected was given a card.
                    </div>
                ) : (
                    <>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                            Left as they were
                        </div>
                        <div style={{ maxHeight: 260, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 10 }}>
                            {notAssigned.map((r) => (
                                <div
                                    key={r.userId}
                                    style={{
                                        display: "flex", justifyContent: "space-between", alignItems: "center",
                                        padding: "8px 12px", borderBottom: `1px solid ${C.borderLight}`, fontSize: 13,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{r.name || `Customer #${r.userId}`}</div>
                                        <div style={{ fontSize: 11, color: C.textLight }}>{r.phoneNo || "—"}</div>
                                    </div>
                                    <div style={{ fontSize: 12, color: C.textMed, textAlign: "right" }}>
                                        {REPORT_LABELS[r.status] || r.status}
                                        {r.membershipRef && (
                                            <div style={{ fontSize: 11, color: C.textLight }}>{r.membershipRef}</div>
                                        )}
                                        {r.reason && <div style={{ fontSize: 11, color: C.textLight }}>{r.reason}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </ModalBody>
            <ModalFooter style={{ borderTop: `1px solid ${C.borderLight}` }}>
                <button type="button" onClick={onDone} style={primaryBtnStyle}>Done</button>
            </ModalFooter>
        </Modal>
    );
};

export default AssignMembershipModal;
